/**
 * ratchet.js — Double Ratchet (Signal spec) per la Forward Secrecy di Nullum.
 *
 * ⚠️ MODULO ISOLATO + DA AUDIRE. Non ancora cablato nei percorsi di messaggio.
 * Implementazione da primitivi (libsodium-sumo + WebCrypto), fedele allo spec:
 *   - DH ratchet su X25519
 *   - KDF_RK = HKDF-SHA256(salt=root, ikm=DH)
 *   - KDF_CK = HMAC-SHA256(ck, 0x01)=msgKey, HMAC-SHA256(ck, 0x02)=nextCk
 *   - cifratura messaggio = XChaCha20-Poly1305 (key+nonce da HKDF(msgKey)), header come AD
 *   - out-of-order via skipped message keys (cache limitata)
 *
 * Multi-device = soluzione (a): lo stato è PER-DEVICE, non sincronizzato
 * (coerente col no-sync di Nullum). Niente fan-out per-device.
 */

import sodium from 'libsodium-wrappers-sumo';

const te = (s) => new TextEncoder().encode(s);
const MAXSKIP = 256;

async function ready() { await sodium.ready; }

/* ── KDF / AEAD primitivi ──────────────────────────────────────────────── */
async function hkdf(salt, ikm, info, L) {
  const baseKey = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt, info: te(info) }, baseKey, L * 8,
  );
  return new Uint8Array(bits);
}
async function hmacByte(key, b) {
  const k = await crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const sig = await crypto.subtle.sign('HMAC', k, new Uint8Array([b]));
  return new Uint8Array(sig);
}
async function KDF_RK(rk, dhOut) {
  const out = await hkdf(rk, dhOut, 'NullumRatchetV1RK', 64);
  return { rk: out.slice(0, 32), ck: out.slice(32, 64) };
}
async function KDF_CK(ck) {
  return { mk: await hmacByte(ck, 0x01), ck: await hmacByte(ck, 0x02) };
}
async function msgKeyMaterial(mk) {
  const out = await hkdf(new Uint8Array(32), mk, 'NullumRatchetV1Msg', 56);
  return { enc: out.slice(0, 32), nonce: out.slice(32, 56) }; // 24-byte XChaCha nonce
}
async function aeadEnc(mk, plaintext, ad) {
  const { enc, nonce } = await msgKeyMaterial(mk);
  return sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, ad, null, nonce, enc);
}
async function aeadDec(mk, ct, ad) {
  const { enc, nonce } = await msgKeyMaterial(mk);
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, ct, ad, nonce, enc);
}

/* ── DH ──────────────────────────────────────────────────────────────── */
export function genKeypair() {
  const kp = sodium.crypto_box_keypair(); // X25519
  return { pub: kp.publicKey, priv: kp.privateKey };
}
const DH = (priv, pub) => sodium.crypto_scalarmult(priv, pub);

/* ── header / helpers ─────────────────────────────────────────────────── */
function headerBytes(h) {
  const b = new Uint8Array(40);
  b.set(h.dh, 0);
  const dv = new DataView(b.buffer);
  dv.setUint32(32, h.pn, true);
  dv.setUint32(36, h.n, true);
  return b;
}

/** Serializza/deserializza l'header per il wire (40 byte: dh32 + pn4 + n4). */
export function serializeHeader(h) { return headerBytes(h); }
export function parseHeader(b) {
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  return { dh: b.slice(0, 32), pn: dv.getUint32(32, true), n: dv.getUint32(36, true) };
}
const concat = (a, b) => { const c = new Uint8Array(a.length + b.length); c.set(a, 0); c.set(b, a.length); return c; };
const eq = (a, b) => !!a && !!b && a.length === b.length && a.every((x, i) => x === b[i]);
const skipId = (dh, n) => sodium.to_hex(dh) + ':' + n;

/* ── init sessione (post X3DH: SK condiviso + prekey di Bob) ───────────── */
export async function initAlice(SK, bobPrekeyPub) {
  await ready();
  const DHs = genKeypair();
  const { rk, ck } = await KDF_RK(SK, DH(DHs.priv, bobPrekeyPub));
  return { DHs, DHr: bobPrekeyPub, RK: rk, CKs: ck, CKr: null, Ns: 0, Nr: 0, PN: 0, skipped: new Map() };
}
export async function initBob(SK, bobPrekeyPair) {
  await ready();
  return { DHs: bobPrekeyPair, DHr: null, RK: SK, CKs: null, CKr: null, Ns: 0, Nr: 0, PN: 0, skipped: new Map() };
}

/* ── encrypt / decrypt ────────────────────────────────────────────────── */
export async function encrypt(state, plaintext, extraAD = new Uint8Array(0)) {
  const { mk, ck } = await KDF_CK(state.CKs);
  state.CKs = ck;
  const header = { dh: state.DHs.pub, pn: state.PN, n: state.Ns };
  state.Ns += 1;
  const ad = concat(extraAD, headerBytes(header));
  const ct = await aeadEnc(mk, typeof plaintext === 'string' ? te(plaintext) : plaintext, ad);
  return { header, ct };
}

async function skipMessageKeys(state, until) {
  if (state.CKr === null) return;
  if (state.Nr + MAXSKIP < until) throw new Error('RATCHET_TOO_MANY_SKIPPED');
  while (state.Nr < until) {
    const { mk, ck } = await KDF_CK(state.CKr);
    state.CKr = ck;
    state.skipped.set(skipId(state.DHr, state.Nr), mk);
    state.Nr += 1;
  }
}
async function dhRatchet(state, header) {
  state.PN = state.Ns; state.Ns = 0; state.Nr = 0;
  state.DHr = header.dh;
  let r = await KDF_RK(state.RK, DH(state.DHs.priv, state.DHr));
  state.RK = r.rk; state.CKr = r.ck;
  state.DHs = genKeypair();
  r = await KDF_RK(state.RK, DH(state.DHs.priv, state.DHr));
  state.RK = r.rk; state.CKs = r.ck;
}

export async function decrypt(state, header, ct, extraAD = new Uint8Array(0)) {
  const ad = concat(extraAD, headerBytes(header));
  const sId = skipId(header.dh, header.n);
  if (state.skipped.has(sId)) {
    const mk = state.skipped.get(sId);
    state.skipped.delete(sId);
    return sodium.to_string(await aeadDec(mk, ct, ad));
  }
  if (!eq(header.dh, state.DHr)) {
    await skipMessageKeys(state, header.pn);
    await dhRatchet(state, header);
  }
  await skipMessageKeys(state, header.n);
  const { mk, ck } = await KDF_CK(state.CKr);
  state.CKr = ck;
  state.Nr += 1;
  return sodium.to_string(await aeadDec(mk, ct, ad));
}
